import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelBenchmarkInsightCompetitorsMapCompetitorsMap from 'lumen/components/hotel/benchmark-insight/competitors/map/competitors-map';
import HotelBenchmarkInsightCompetitorsTableCompetitors from 'lumen/components/hotel/benchmark-insight/competitors/table-competitors';
import type HotelSettingsCompetitorsBenchmarkCompsetController from 'lumen/controllers/hotel/settings/competitors/benchmark/compset';
import type HotelSettingsCompetitorsBenchmarkCompsetRoute from 'lumen/routes/hotel/settings/competitors/benchmark/compset';
import type { ModelFrom } from 'lumen/utils/routes';

interface HotelSettingsCompetitorsBenchmarkCompsetSignature {
  Args: {
    controller: HotelSettingsCompetitorsBenchmarkCompsetController;
    model: ModelFrom<HotelSettingsCompetitorsBenchmarkCompsetRoute>;
  };
}

<template>
  <div class="compset flex {{if @controller.showEmptyState 'h-full'}}">
    {{#if @controller.showEmptyState}}
      <div
        class="rounded-200 bg-elevation-surface flex h-full w-full items-center justify-center shadow-neutral-100"
      >
        {{#if @model.compset.last.value}}
          <PrismMessage
            @description={{@controller.emptyStateDescription}}
            @illustration="200x200_benchmark-focus"
            @title={{@controller.emptyStateTitle}}
            data-test-id="compset-empty-state"
          >
            <:primaryAction>
              {{#if @controller.access.hasBenchmarkInsightSettingsCompsetEdit}}
                <PrismButton
                  @buttonType="brand"
                  @label={{t
                    "settings.competitors.benchmark.compset.empty_state.action"
                  }}
                  {{on "click" @controller.onClickCreateCompset}}
                  data-test-id="compset-empty-state-action"
                />
              {{/if}}
            </:primaryAction>
          </PrismMessage>
        {{else}}
          <PrismMessage
            @description=""
            @title={{t "settings.competitors.benchmark.compset.error"}}
          />
        {{/if}}
      </div>
    {{else}}
      <div class="flex flex-col gap-600">
        {{#if @controller.showInvalidCompsetBanner}}
          <PrismBanner
            @bannerMessage={{t
              "benchmark_insight.banners.compset_validity.compset.message"
            }}
            @bannerStyle="warning"
            @bannerTitle={{t
              "benchmark_insight.banners.compset_validity.compset.title"
            }}
            @icon="warning"
            data-test-id="compset-validity-banner"
          />
        {{else if @controller.showNewCompetitorAvailableBanner}}
          <PrismBanner
            @bannerMessage={{@controller.newCompetitorAvailableBannerMessage}}
            @bannerStyle="success"
            @icon="circle-checkmark"
            data-test-id="compset-new-competitor-available-banner"
          />
        {{/if}}

        <div class="grid h-full w-full grid-cols-2 gap-1000">
          <div class="flex flex-col gap-400">
            <PrismCard
              @contentLayout="noPadding"
              @isSecondary={{true}}
              class="prism-card--with-table-competitors prism-card--with-border"
            >
              <:header as |Header|>
                <div class="text-200--regular flex items-center gap-200">
                  <Header.title>
                    {{t
                      "settings.competitors.benchmark.compset.current_compset"
                    }}
                  </Header.title>

                  {{@controller.formattedNumberOfCompetitors}}
                </div>
              </:header>

              <:content>
                <HotelBenchmarkInsightCompetitorsTableCompetitors
                  @competitors={{@controller.competitors}}
                  @isLoading={{@controller.isLoading}}
                  @isReadOnly={{true}}
                />
              </:content>
            </PrismCard>

            {{#if @controller.unusedDesiredCompetitors.length}}
              <PrismCard
                @contentLayout="noPadding"
                @isSecondary={{true}}
                class="prism-card--with-table-competitors prism-card--with-border"
                data-test-id="compset-unused-competitors"
              >
                <:header as |Header|>
                  <div
                    class="text-200--regular flex flex-row items-center gap-200"
                  >
                    <Header.title>
                      {{t
                        "settings.competitors.benchmark.compset.builder.not_used"
                      }}
                    </Header.title>

                    <GeneralIconWithTooltip
                      @size="small"
                      @tooltipContent={{@controller.unusedDesiredCompetitorsTooltipText}}
                    />
                  </div>
                </:header>

                <:content>
                  <HotelBenchmarkInsightCompetitorsTableCompetitors
                    {{! @glint-ignore: Class inheritance }}
                    @competitors={{@controller.unusedDesiredCompetitors}}
                    @isLoading={{@controller.model.desiredCompset.isRunning}}
                    @isReadOnly={{true}}
                    @isRowHoverDisabled={{true}}
                    @showDesiredCompetitorAvailability={{true}}
                  />
                </:content>
              </PrismCard>
            {{/if}}
          </div>

          <div class="grid h-full w-full">
            <HotelBenchmarkInsightCompetitorsMapCompetitorsMap
              @compset={{@controller.competitors}}
            />
          </div>
        </div>
      </div>
    {{/if}}
  </div>

  {{outlet}}
</template> satisfies TOC<HotelSettingsCompetitorsBenchmarkCompsetSignature>;
